import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useCallback, useRef, useState, type ReactNode } from 'react';

const productionSiteKey = '0x4AAAAAAFYUIl8gTUS9ous-';
const testSiteKey = '1x00000000000000000000AA';
const responseTimeoutMs = 30_000;
const widgetLoadTimeoutMs = 15_000;

const siteKey =
  import.meta.env.MODE === 'e2e'
    ? testSiteKey
    : (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? productionSiteKey);

type HumanVerification = {
  getToken: () => Promise<string>;
  isVerifying: boolean;
  reset: () => void;
  widget: ReactNode;
};

type WidgetLoad = {
  promise: Promise<void>;
  resolve: () => void;
};

const createWidgetLoad = (): WidgetLoad => {
  let resolve: () => void = () => undefined;
  const promise = new Promise<void>((resolvePromise) => {
    resolve = resolvePromise;
  });

  return { promise, resolve };
};

const withTimeout = async (promise: Promise<void>, timeoutMs: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Human verification timed out')),
      timeoutMs,
    );
  });

  try {
    await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Turnstile is mounted on the first `getToken` call rather than on render, so
 * pages with forms don't pay for Cloudflare's script and iframe on load.
 * Load failures are bounded by a timeout rather than `onError`, because
 * Turnstile reports retryable errors there and then retries on its own.
 */
const useHumanVerification = (action: string): HumanVerification => {
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const widgetLoadRef = useRef<WidgetLoad | null>(null);
  const [isRequested, setIsRequested] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const loadWidget = useCallback(async () => {
    if (!widgetLoadRef.current) {
      widgetLoadRef.current = createWidgetLoad();
      setIsRequested(true);
    }

    try {
      await withTimeout(widgetLoadRef.current.promise, widgetLoadTimeoutMs);
    } catch (error) {
      widgetLoadRef.current = null;
      setIsRequested(false);
      throw error;
    }
  }, []);

  const getToken = useCallback(async () => {
    setIsVerifying(true);
    try {
      await loadWidget();

      if (!turnstileRef.current) {
        throw new Error('Human verification is not ready');
      }

      turnstileRef.current.execute();
      const token =
        await turnstileRef.current.getResponsePromise(responseTimeoutMs);

      if (!token) {
        throw new Error('Human verification failed');
      }

      return token;
    } finally {
      setIsVerifying(false);
    }
  }, [loadWidget]);

  const reset = useCallback(() => {
    turnstileRef.current?.reset();
  }, []);

  const widget: ReactNode = isRequested ? (
    <div className="fixed right-4 bottom-4 z-50">
      <Turnstile
        onWidgetLoad={() => widgetLoadRef.current?.resolve()}
        options={{
          action,
          execution: 'execute',
          responseField: false,
          size: 'invisible',
        }}
        ref={turnstileRef}
        siteKey={siteKey}
      />
    </div>
  ) : null;

  return { getToken, isVerifying, reset, widget };
};

export default useHumanVerification;
